import { useMemo } from 'react';

import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query';
import flatMap from 'lodash/flatMap';

import { POLLING_DELAY_MS } from '@cast/constants';
import { ListClusterOptimizationsResponse } from '@cast/types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const CLUSTER_OPTIMIZATIONS_QUERY_KEY = 'cluster-optimizations';

export const getClusterOptimizationsQueryKey = (params: {
  organizationId: string;
  clusterIds?: string[];
  tourStepId?: string | null;
}) =>
  [
    CLUSTER_OPTIMIZATIONS_QUERY_KEY,
    params.organizationId,
    params.clusterIds ?? [],
    params.tourStepId,
  ] as const;

type ClusterOptimizationsParams = {
  organizationId: string;
  clusterIds?: string[];
  tourStepId?: string | null;
};

export const getClusterOptimizationsQueryOptions = ({
  organizationId,
  clusterIds,
  tourStepId,
  enabled = true,
}: ClusterOptimizationsParams & { enabled?: boolean }) =>
  infiniteQueryOptions({
    queryKey: getClusterOptimizationsQueryKey({
      organizationId,
      clusterIds,
      tourStepId,
    }),
    queryFn: async ({ pageParam }) => {
      const { data } = await apiClient.optimization.listClusterOptimizations(
        {
          organizationId,
          runClusterOptimizationsReportRequestFilter: {
            clusterIds: clusterIds ?? [],
          },
          pageCursor: pageParam,
          pageLimit: '99999',
        },
        { timeout: 120_000 }
      );

      return data;
    },
    initialPageParam: '',
    getNextPageParam: (lastPage: ListClusterOptimizationsResponse) =>
      lastPage.nextPageCursor ?? undefined,
    refetchInterval: POLLING_DELAY_MS,
    enabled: makeIsEnabled(!!organizationId, enabled),
  });

export type ClusterOptimizationsQueryArgs = {
  enabled?: boolean;
  organizationId?: string;
  clusterIds?: string[];
  tourStepId?: string | null;
};

export const useClusterOptimizationsQuery = ({
  organizationId: _organizationId,
  clusterIds,
  tourStepId,
  enabled = true,
}: ClusterOptimizationsQueryArgs = {}) => {
  const currentOrgId = useOrganizations().currentOrganization?.id;
  const organizationId = _organizationId ?? currentOrgId ?? '';

  const { data, ...query } = useInfiniteQuery(
    getClusterOptimizationsQueryOptions({
      organizationId,
      clusterIds,
      tourStepId,
      enabled,
    })
  );

  const flattenedData = useMemo(
    () =>
      data?.pages
        ? flatMap(data.pages, (page) => page?.items ?? [])
        : undefined,
    [data]
  );

  return {
    ...query,
    data: flattenedData,
  };
};
